
import { Pencil, Trash2 } from 'lucide-react';
import eye from 'images/icons/company/eye.svg';
import modal_open from 'images/icons/company/modal_open.svg';
import close from 'images/icons/orders/close.svg';
import * as moment from 'moment';
import * as React from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AAssortments } from '../../atoms/assortment';
import { ACampaigns } from '../../atoms/campaignAtom';
import { ABrand } from '../../atoms/filter/BrandFiltersAtom';
import { AFormFields, Field, getFields } from '../../atoms/forms';
import { AProducts } from '../../atoms/product';
import { PanelSelector } from '../../components_v2/Selector/PanelSelector';
import { UserBlock } from '../../components_v2/dropdown/DropdownOwners';
import Input from '../../components_v2/input/Input';
import Popup from '../../components_v2/popup/Popup';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { PopupMode, PopupProps } from '../../components_v2/popup/model/Model';
import { HeaderTitle } from '../../components_v2/popup/style/PopupCreationStyle';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { DefaultImage } from '../../styles/global/css/GlobalImage';
import { Loader } from '../../styles/global/css/GlobalLoader';
import { DefaultText, DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { PrimaryColor, BorderColor, DarkGreySidely2, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import StyledFieldEditor from '../../utils/fieldEditor';
import { TitleAndChild } from '../client-companies/popup/Detail';
import { ButtonAction, FullOpen, HeaderLeft } from '../client-companies/style/PopupStyle';
import { creationPopupInputStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { LoadingStateEnum } from '../import/model';
import Restricted from '../permissions/Restricted';
import RestrictedSuperAdmin from '../permissions/RestrictedSuperAdmin';
import { ModalState } from '../products/model';
import ProductPopup from '../products/product';
import { FlexDiv } from '../products/style';
import { FieldDisplayer } from './FieldDisplayer';
import { Instance, InstanceField, View, getFormViews, getInstanceByUuid, getInstanceFields, postFieldValue, putFieldValue, putInstanceProgression } from './actions';
import { launchToastError } from './visual-form-editor/visualFormEditor';
import { ComponentType, Screen } from './jsonValidator';
import { TemplateRenderer } from './templateRenderer';

const Td = styled.td``;

const ThreadContainer = styled.div<{ hovered: boolean }>`
	width: 100%;
	margin-left: 7px;
	padding: 0 0 10px 15px;
	border-left: 1px solid  ${p => p.hovered ? PrimaryColor : BorderColor};
`;

const StyledDiv = styled.div`
${DefaultText}
font-size: 12px;
	height: 100%;
	width: 100%;
	h4 {
		margin: 0;
	}
	table {
		border-collapse: collapse;
		font-size: 0.8rem;
		letter-spacing: 1px;
	  }

	  th, td:first-of-type {
		font-size: 12px;
		color: ${SidelyBlack};
	  }

	  th,
	  td {
		border: 1px solid  ${BorderColor};
		padding: 8px 10px;
	  }

	  td:last-of-type {
		text-align: center;
	  }

	  tfoot th {
		text-align: right;
	  }

	  tfoot td {
		font-weight: bold;
	  }
`;

const ProgressBarStyle = styled.div`
	width: 100%;
	height: 11px;
	.progress .progress-bar {
		width: 100%;
	}
	.progress {
		width: 100%;
	}
`;

type LocalTemplate = {
	name: string, screens: Screen[]
}

interface FormInstanceContextType {
	hoverHistory: string[],
	setHoverHistory: React.Dispatch<React.SetStateAction<string[]>>
	productPopupState: ModalState<number>
	setProductPopupState: React.Dispatch<React.SetStateAction<ModalState<number>>>
	template: LocalTemplate | undefined
	setTemplate: React.Dispatch<React.SetStateAction<LocalTemplate | undefined>>
	formInstanceUuid: string | undefined
}

const defaultBehaviour: FormInstanceContextType = {
	hoverHistory: [],
	setHoverHistory: () => undefined,
	productPopupState: { isOpen: false },
	setProductPopupState: () => undefined,
	template: undefined,
	setTemplate: () => undefined,
	formInstanceUuid: undefined
};

const FormInstanceContext = React.createContext<FormInstanceContextType>(defaultBehaviour);

function FormInstanceProvider(props: {
	children: React.ReactNode,
	formInstanceUuid?: string | undefined
}) {
	const [hoverHistory, setHoverHistory] = React.useState<string[]>([]);
	const [productPopupState, setProductPopupState] = React.useState<ModalState<number>>({ isOpen: false });
	const [template, setTemplate] = React.useState<LocalTemplate>();
	const { formInstanceUuid } = props;

	return <FormInstanceContext.Provider value={{ hoverHistory, setHoverHistory, productPopupState, setProductPopupState, template, setTemplate, formInstanceUuid }}>
		{props.children}
	</FormInstanceContext.Provider>;
}

type FormInstanceContentProps = { instanceUuid: string, onClickOut?: () => void, isFullOpen: boolean, handleResizePopup: () => void }

interface InstancePopupProps extends PopupProps {
	modalState: ModalState<string>;
}

export function InstancePopup(props: InstancePopupProps) {
	const [isFullOpen, setIsFullOpen] = React.useState(props.modalState.fullOpenMode ?? true);
	React.useEffect(() => setIsFullOpen(props.modalState?.fullOpenMode ?? isFullOpen), [props.modalState]);
	return <Popup
		{...props}
		isOpen={props.isOpen}
		popupMode={isFullOpen ? PopupMode.Default : PopupMode.Details}
		content={props.modalState.data ? <FormInstanceContent
			instanceUuid={props.modalState.data}
			isFullOpen={isFullOpen}
			onClickOut={props.onClickOut}
			handleResizePopup={() => setIsFullOpen(b => !b)}
		/> : <></>}
		onClickOut={props.onClickOut}
	/>;
}

function FormInstanceContent(props: FormInstanceContentProps) {
	return <FormInstanceProvider formInstanceUuid={props.instanceUuid}>
		<_FormInstanceContent {...props} />
	</FormInstanceProvider>;
}

const FieldValues = styled.div`
	display: grid;
	padding-bottom: 4px;
	grid-template-columns: 0.25fr 1fr;
	& > div {
		min-height: 35px;
		border-bottom: 1px solid ${BorderColor};
	}
	& > div:nth-child(odd) {
		font-weight: 300;
		padding: 5px 10px 5px 5px;
		border-right: 1px solid ${BorderColor};
	}
	& > div:nth-child(even) {
		padding: 5px 10px;
		color: ${SidelyBlack};
	}
	& > div:nth-last-child(-n+2) {
		border-bottom: none;
	}
`;

const ResponsiveGridContainer = styled.div<{ fullOpen: boolean, max: string }>`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(${p => p.max}, 1fr));
	overflow-y: auto;
	gap: ${p => p.fullOpen ? 10 : 0}px;
`;

const DefaultViewContainer = styled.div`
	padding: 10px;
`;

function _FormInstanceContent(props: FormInstanceContentProps) {
	const [fieldValues, setFieldValues] = React.useState<InstanceField[]>([]);
	const [instance, setInstance] = React.useState<Instance>();
	const { productPopupState, setProductPopupState, setTemplate } = React.useContext(FormInstanceContext);
	const [views, setViews] = React.useState<View[]>([]);
	const [panelIndex, setPanelIndex] = React.useState(0);
	const [editMode, setEditMode] = React.useState(false);
	const [refresh, setRefresh] = React.useState(false);
	const [progressionDraft, setProgressionDraft] = React.useState<string>('');
	// a draft outside 0..100 is refused by the server too, so the button stays
	// disabled rather than letting the popup show a broken bar
	const validProgression = /^\d{1,3}$/.test(progressionDraft) && Number(progressionDraft) <= 100;

	React.useEffect(() => {
		getInstanceFields(props.instanceUuid).then(setFieldValues);
		getInstanceByUuid(props.instanceUuid).then(res => {
			setInstance(res);
			setProgressionDraft(res.progression.toString());
			setTemplate({ name: res.name, screens: res.screens });
		});
		setRefresh(false);
	}, [editMode, refresh]);
	React.useEffect(() => {
		if (!instance) return;
		getFormViews(instance.form_id).then(setViews);
	}, [instance]);
	if (!instance) return <></>;
	const dateFormatted = moment.utc(instance.made_at).local().format('L LTS');
	const inputStyle = {};

	return <>
		<FlexDiv flow='column' align='stretch' margin='0 0 0 15px' height='100%' overflow='auto'>
			<HeaderLeft style={{ position: 'sticky', top: 0, zIndex: 1, background: SurfaceColor, marginLeft: '-15px', marginBottom: '10px' }}>
				<FlexDiv
					width="100%" justify="space-evenly" gap="20px" padding="5px 10px 10px 10px"
					borderBottom={!(views.length != 0 && props.isFullOpen) ? `1px solid ${BorderColor}` : undefined}>
					<DefaultImage cursor="pointer" src={close} onClick={props.onClickOut} />
					<HeaderTitle>{`${instance.name} -- ${dateFormatted}`}</HeaderTitle>
					{panelIndex != 0 && props.isFullOpen && <DefaultButton
						buttonStyle={2}
						margin='0'
						onClick={() => {
							window.frames['printf'].focus();
							window.frames['printf'].print();
						}}><Translate id='global.download' /></DefaultButton>}
					<FlexDiv gap='10px'>
						{panelIndex == 0 && props.isFullOpen && <Restricted to={{ objectAction: 'UpdateFreeForm' }}>
							<div style={{ cursor: 'pointer' }}><Pencil onClick={() => setEditMode(!editMode)} color={editMode ? PrimaryColor : SidelyBlack} /></div>
						</Restricted>
						}
						<FullOpen src={modal_open} isActive={props.isFullOpen} onClick={props.handleResizePopup} />
					</FlexDiv>
				</FlexDiv>
			</HeaderLeft>
			<StyledDiv>
				{views.length != 0 && props.isFullOpen && <PanelSelector
					borderBottom
					panels={[{ label: translateToString('views.default_view') }, ...views.map(v => ({ label: v.name }))]}
					activeIndex={panelIndex}
					onClick={(_, index) => {
						setPanelIndex(index);
					}}
				/>}
				{panelIndex != 0 && props.isFullOpen ?
					<TemplateRenderer form_instance_uuid={props.instanceUuid} instance={instance} fields={fieldValues} template={views[panelIndex - 1].template} />
					:
					<DefaultViewContainer>
						<ResponsiveGridContainer fullOpen={props.isFullOpen} max={'200px'}>
							<Input
								inputStyle={{ ...inputStyle, ...creationPopupInputStyle }}
								name="company_name"
								type="text"
								label={translateToString('shelf_audit.popup.company_name')}
								value={instance.linked_company_name}
								disableChange
							/>
							<UserBlock userId={instance.created_by} />
						</ResponsiveGridContainer>
						<ResponsiveGridContainer fullOpen={props.isFullOpen} max={props.isFullOpen ? '150px' : '200px'}>
							<Input
								inputStyle={{ ...inputStyle, ...creationPopupInputStyle }}
								name="date"
								type="text"
								label={translateToString('started_on')}
								value={moment.utc(instance.started_at).local().format('L LTS')}
								disableChange
							/>
							<Input
								inputStyle={{ ...inputStyle, ...creationPopupInputStyle }}
								name="date"
								type="text"
								label={translateToString('ended_on')}
								value={dateFormatted}
								disableChange
							/>
							<RestrictedSuperAdmin>
								<Input
									inputStyle={{ ...inputStyle, ...creationPopupInputStyle }}
									name="date"
									type="text"
									label={translateToString('synched_on')}
									value={moment.utc(instance.created_at).local().format('L LTS')}
									disableChange
								/>
							</RestrictedSuperAdmin>
							<Input
								inputStyle={{ ...inputStyle, ...creationPopupInputStyle }}
								name="date"
								type="text"
								label={translateToString('updated_on')}
								value={instance.updated_at ? moment.utc(instance.updated_at).local().format('L LTS') : '-'}
								disableChange
							/>
						</ResponsiveGridContainer>
						<div style={{ padding: '10px 0' }}>
							<p style={{ paddingBottom: '5px' }} >{translateToString('progress')}: {instance.progression}%</p>
							<ProgressBarStyle>
								<ProgressBar
									now={instance.progression}
									variant={instance.progression == 100 ? 'success' : instance.progression > 45 ? 'warning' : 'danger'}
									min={0}
									max={100}
								/>
							</ProgressBarStyle>
							{/* the pen edits the answers; it edits their completion too, for
							    the visit a skipped screen left short of 100% */}
							{editMode && <FlexDiv gap='10px' align='center' margin='10px 0 0 0'>
								<Input
									inputStyle={{ ...inputStyle, ...creationPopupInputStyle, width: '120px' }}
									name='progression'
									type='number'
									min={0}
									max={100}
									label={translateToString('progress')}
									value={progressionDraft}
									onChange={(value: string) => setProgressionDraft(value)}
								/>
								<DefaultButton
									margin='0'
									disabled={!validProgression || Number(progressionDraft) === instance.progression}
									onClick={() => putInstanceProgression(props.instanceUuid, Number(progressionDraft))
										.then(() => setRefresh(true))
										.catch(e => {
											console.error(e);
											launchToastError(translateToString('error_please_retry'));
										})}
								><Translate id='save' /></DefaultButton>
							</FlexDiv>}
						</div>
						{props.isFullOpen && <FormTemplateGenericDisplayerPerFields fieldValues={JSON.parse(JSON.stringify(fieldValues))} editMode={editMode} setRefresh={setRefresh} />}
					</DefaultViewContainer>
				}
			</StyledDiv>
		</FlexDiv>
		<ProductPopup isOpen={productPopupState.isOpen} setIsOpen={(isOpen) => setProductPopupState({ isOpen })} productId={productPopupState.data} />
	</>;
}

function findDispatcherTypeFromMetadataKey(template: LocalTemplate | undefined, key: string): ComponentType | undefined {
	const type = template?.screens.reduce((acc, screen) => {
		if (acc) return acc;
		return screen.components.find(c => c.data?.['name'] === key);
	}, undefined)?.type;
	if (type && type !== 'table' && type !== 'card_list') return type;
	if (key === 'key') return undefined;
	if (key === 'catalogue') return 'catalogue_dispatcher';
	if (key === 'product') return 'product_dispatcher';
	if (key === 'brand') return 'brand_dispatcher';
	return type ?? 'dispatcher';
}
function getComponentTypePrecedence(type: ComponentType | undefined): number {
	if (type === 'catalogue_dispatcher') return 4;
	if (type === 'dispatcher') return 3;
	if (type === 'product_dispatcher') return 2;
	return 0;
}

function buildTree(componentMap: { [name: string]: T }, fieldValues: InstanceField[], hiddenFields: Set<number>): Tree {
	const tree: Tree = {};
	fieldValues.forEach(fv => {
		if (hiddenFields.has(fv.id)) return;
		const obj: Tree = Object.entries(fv.metadata).map(([key, value]) => {
			const order = componentMap[key]?.precedence ?? -1;
			return { key, value, order };
		}).sort((a, b) => b.order - a.order).reduce((curr_obj, { key, value }) => {
			if (!value) return curr_obj;
			curr_obj[key] ??= {};
			curr_obj[key][value] ??= {};
			return curr_obj[key][value];
		}, tree);
		obj.veryLongStringSoCustomersCannotUseThisMetadataByMistake ??= [];
		obj.veryLongStringSoCustomersCannotUseThisMetadataByMistake.push(fv);
	});
	return tree;
}

type T = { name: string, type: ComponentType | undefined, precedence: number };
type Tree = { veryLongStringSoCustomersCannotUseThisMetadataByMistake?: InstanceField[] } & { [metadataName: string]: { [metadataValue: string]: Tree } };


function getRecusiveAllField(tree: Tree): { [key: number]: string } {
	const res: { [key: number]: string } = {};
	tree.veryLongStringSoCustomersCannotUseThisMetadataByMistake?.forEach(fv => {
		res[fv.id] = fv.name;
	});
	Object.entries(tree).forEach(([, value]) => {
		if (Array.isArray(value)) return;
		Object.entries(value).forEach(([, newTree]) => {
			const fields = getRecusiveAllField(newTree);
			Object.entries(fields).forEach(([id, name]) => {
				res[parseInt(id)] = name;
			});
		});
	});
	return res;
}

function getRecursiveAllFieldValues(tree: Tree, metadataName: string): { metadataValue: string, fields: InstanceField[] }[] {
	const res: { metadataValue: string, fields: InstanceField[] }[] = [];
	Object.entries(tree).forEach(([, value]) => {
		if (Array.isArray(value)) {
			res.push({ metadataValue: value[0].metadata[metadataName], fields: value });
			return;
		}
		Object.entries(value).forEach(([, newTree]) => {
			res.push(...getRecursiveAllFieldValues(newTree, metadataName));
		});
	});
	return res;
}

function EditableInstanceField(props: { field: Field, fieldValue: InstanceField, isEditable: boolean, justifyLeft?: boolean, loadingStateGeneral: LoadingStateEnum | undefined, onDelete?: () => void, onSave?: (value: unknown) => void }) {
	const { field, fieldValue, onSave, isEditable } = props;
	const [fieldState, setFieldState] = React.useState<any>(fieldValue.value);
	const [hoverState, setHoverState] = React.useState(false);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();
	const [confirmDeletePopupOpen, setConfirmDeletePopupOpen] = React.useState(false);
	const [unsupportedField, setUnsupportedField] = React.useState(false);
	React.useEffect(() => {
		setFieldState(fieldValue.value);
	}, [fieldValue.value]);

	React.useEffect(() => {
		if (props.loadingStateGeneral && props.loadingStateGeneral !== LoadingStateEnum.LOADING) {
			setLoadingState(props.loadingStateGeneral);
		}
	}, [props.loadingStateGeneral]);

	return loadingState === LoadingStateEnum.LOADING ? <Loader width='30px' centerHorizontal /> :
		<div>
			<Popup popupMode={PopupMode.Centered} isOpen={confirmDeletePopupOpen} onClickOut={() => setConfirmDeletePopupOpen(false)} popupStyle={{ width: '320px', height: '240px' }}>
				<FlexDiv width='100%' height='100%' justify='center' gap='20px' flow='column' padding='10px 20px' fontSize='13px'>
					<div style={{ textAlign: 'center' }}><Translate id='tool_bar.forms.instance.delete_confirmation' /></div>
					<FlexDiv width='100%' justify='center' gap='10px'>
						<DefaultButton buttonStyle={ButtonStyle.White}
							onClick={() => setConfirmDeletePopupOpen(false)}>
							<Translate id='cancel' />
						</DefaultButton>
						<DefaultButton buttonStyle={ButtonStyle.Error}
							onClick={() => {
								setFieldState(null);
								props.onDelete?.();
								setConfirmDeletePopupOpen(false);
							}}>
							<Translate id='delete' />
						</DefaultButton>
					</FlexDiv>
				</FlexDiv>
			</Popup>
			{isEditable && !unsupportedField ?
				<FlexDiv width='100%' justify='space-between' gap='5px' onMouseEnter={() => setHoverState(true)} onMouseLeave={() => setHoverState(false)}>
					<StyledFieldEditor
						field={field}
						value={fieldState}
						onSave={(value) => {
							setLoadingState(LoadingStateEnum.LOADING);
							onSave && onSave(value);
						}}
						onChange={setFieldState}
						inputStyle={{ border: 'none', width: '100%', containerWidth: '100%' }}
						setUnsupportedField={setUnsupportedField}
					/>
					<div style={{ cursor: 'pointer', visibility: hoverState ? 'visible' : 'hidden' }}><Trash2 size={16} onClick={() => setConfirmDeletePopupOpen(true)} /></div>
				</FlexDiv>
				:
				<div>
					<FieldDisplayer
						field_type={field.type}
						value={fieldState}
						fieldId={fieldValue.id}
						metadata={fieldValue.metadata}
						justifyLeft={props.justifyLeft}
					/>
				</div>
			}
		</div>;
}

function TreeDisplayer(props: { tree: Tree, fieldMap: { [key: string]: Field }, componentMap: { [name: string]: T }, editMode: boolean, allFieldsNoForm: Field[], setRefresh: (refresh: boolean) => void }) {
	const { tree, fieldMap, componentMap, editMode, allFieldsNoForm, setRefresh } = props;
	const campaigns = useRecoilValue(ACampaigns);
	const assortments = useRecoilValue(AAssortments);
	const products = useRecoilValue(AProducts);
	const brands = useRecoilValue(ABrand);
	const [loadingStateGeneral, setLoadingStateGeneral] = React.useState<LoadingStateEnum>();
	const { setProductPopupState, formInstanceUuid } = React.useContext(FormInstanceContext);
	const sortedTreeMetadata = tree.veryLongStringSoCustomersCannotUseThisMetadataByMistake?.reduce((acc: { [screen: string]: InstanceField[] }, fv) => {
		const screen = fieldMap[fv.id]?.screen;
		if (!screen) {
			acc['default'] ??= [];
			acc['default'].push(fv);
			return acc;
		}
		if (!acc[screen]) {
			acc[screen] = [];
		}
		acc[screen].push(fv);
		return acc;
	}, {});
	return <>
		{sortedTreeMetadata &&
			<div>
				{Object.entries(sortedTreeMetadata)
					.sort(([a], [b]) => {
						if (a === 'default' || b === 'no-screen') return 1;
						if (b === 'default' || a === 'no-screen') return -1;
						return a.localeCompare(b);
					})
					.map(([screen, fieldValues]) => {
						return <FlexDiv flow='column' gap='4px' align='stretch' key={screen}>
							{screen !== 'no-screen' &&
								<div style={{ fontSize: '13px', fontWeight: '600' }}>{screen === 'default' ? translateToString('other') : screen}</div>
							}
							<FieldValues>
								{fieldValues.sort((a, b) => a.name.localeCompare(b.name)).map(fv => {
									return <React.Fragment key={fv.id}>
										<DefaultTextDiv color={DarkGreySidely2}>{fv.name}</DefaultTextDiv>
										<EditableInstanceField
											field={fieldMap[fv.id]}
											fieldValue={fv}
											isEditable={editMode}
											loadingStateGeneral={loadingStateGeneral}
											justifyLeft
											onSave={async(value) => {
												if (!formInstanceUuid) return;
												if (value === null) return;
												setLoadingStateGeneral(LoadingStateEnum.LOADING);
												putFieldValue({
													uuid: fv.uuid,
													field_id: fv.id,
													form_instance_id: formInstanceUuid,
													value: value,
													metadata: fv.metadata
												}).then(() => {
													setLoadingStateGeneral(LoadingStateEnum.SUCCESS);
												});
											}}
											onDelete={async() => {
												if (!formInstanceUuid) return;
												await putFieldValue({
													uuid: fv.uuid,
													field_id: fv.id,
													form_instance_id: formInstanceUuid,
													value: null,
													metadata: fv.metadata
												});
											}}
										/>
									</React.Fragment>;
								})}
							</FieldValues>
						</FlexDiv>;
					})}
			</div>
		}
		{Object.entries(tree).flatMap(([metadataName, value]) => {
			if (Array.isArray(value) || componentMap[metadataName]?.type === 'product_dispatcher') return [];
			return Object.entries(value).map(([metadataValue, newTree]) => {
				let title: string | undefined;
				switch (componentMap[metadataName]?.type) {
					case 'campaign_dispatcher':
						title = campaigns[metadataValue]?.name;
						break;
					case 'catalogue_dispatcher':
						title = assortments.find(a => a.id.toString() == metadataValue)?.name;
						break;
					case 'brand_dispatcher': {
						let fallback = `${metadataName.charAt(0).toUpperCase()}${metadataName.slice(1)}`;
						if (metadataValue !== 'true') fallback += `: ${metadataValue}`;
						title = brands ? brands.find(b => b.id.toString() == metadataValue)?.name ?? fallback : fallback;
						break;
					}
					default: {
						title = `${metadataName.charAt(0).toUpperCase()}${metadataName.slice(1)}`;
						if (metadataValue !== 'true') title += `: ${metadataValue}`;
					}
				}
				return { metadataName, metadataValue, newTree, title };
			});
		}).sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')).map(({ metadataName, metadataValue, newTree, title }) =>
			<TitleAndChild defaultOpen title={title} key={`${metadataName}=${metadataValue}`}>
				<ThreadContainer hovered={false}>
					<TreeDisplayer tree={newTree} fieldMap={fieldMap} componentMap={componentMap} editMode={editMode} allFieldsNoForm={allFieldsNoForm} setRefresh={setRefresh} />
				</ThreadContainer>
			</TitleAndChild>
		)}
		{Object.entries(tree).map(([metadataName, value]) => {
			if (Array.isArray(value)) return undefined;
			if (componentMap[metadataName]?.type === 'product_dispatcher') {
				const allFieldValues: { metadataValue: string, fields: InstanceField[] }[] = [];
				const allFields = Object.values(value).reduce((acc: { [key: number]: string }, tree) => {
					const fields = getRecusiveAllField(tree);
					allFieldValues.push(...getRecursiveAllFieldValues(tree, metadataName));
					Object.entries(fields).forEach(([id, name]) => {
						acc[parseInt(id)] = name;
					});
					return acc;
				}, {});
				const allFieldsArray = Object.entries(allFields).map(([id, name]) => ({ id: parseInt(id), name })).sort((a, b) => a.name.localeCompare(b.name));
				return <table key={`${metadataName}`}>
					<thead>
						<tr>
							<th>{translateToString('general.products')}</th>
							{allFieldsArray.map(({ id, name }) => <th key={`HEADER[${id}]`}>{name}</th>)}
						</tr>
					</thead>
					<tbody>
						{
							allFieldValues.map(({ metadataValue, fields }, i) => {
								const product = products.find(p => p.uuid == metadataValue);
								const fieldsMetadata = fields.find(f => f.metadata[metadataName] == metadataValue);
								return <tr key={`COLUMN[${i}]`}>
									<Td>
										{product?.name}
										<ButtonAction hided style={{ float: 'right' }} parent={Td} src={eye} alt="phone" onClick={() => {
											if (product) setProductPopupState({ isOpen: true, data: product.id });
										}} />
									</Td>
									{allFieldsArray.map(({ id }, i) => {
										const field = fields.find(fv => fv.id === id);
										if ((!field || field?.value === undefined) && allFieldsNoForm && editMode) {
											const fieldWitoutValue = allFieldsNoForm.find(f => f.id === id);
											if (!fieldWitoutValue || !fieldsMetadata) return <td key={`LAYOUT[${i}]`}>-</td>;
											return <td key={`LAYOUT[${i}]`}>
												<EditableInstanceField
													field={fieldWitoutValue}
													fieldValue={{ ...fieldWitoutValue, uuid: '', metadata: fieldsMetadata.metadata, value: '' }}
													isEditable={editMode}
													loadingStateGeneral={loadingStateGeneral}
													onSave={async(value) => {
														if (!formInstanceUuid) return;
														if (value === null) return;
														setLoadingStateGeneral(LoadingStateEnum.LOADING);
														postFieldValue({
															field_id: fieldWitoutValue.id,
															form_instance_id: formInstanceUuid,
															value: value,
															metadata: fieldsMetadata.metadata
														}).then(() => {
															setRefresh(true);
															setLoadingStateGeneral(LoadingStateEnum.SUCCESS);
														});
													}} />
											</td>;
										}
										return <td key={`LAYOUT[${i}]`}>
											{field?.value != undefined ?
												<EditableInstanceField
													field={fieldMap[field.id]}
													fieldValue={field}
													isEditable={editMode}
													loadingStateGeneral={loadingStateGeneral}
													onSave={async(value) => {
														if (!formInstanceUuid) return;
														if (value === null) return;
														setLoadingStateGeneral(LoadingStateEnum.LOADING);
														await putFieldValue({
															uuid: field.uuid,
															field_id: field.id,
															form_instance_id: formInstanceUuid,
															value: value,
															metadata: field.metadata
														}).then(() => {
															setLoadingStateGeneral(LoadingStateEnum.SUCCESS);
														});
													}} />
												:
												<FlexDiv justify='center'>-</FlexDiv>
											}
										</td>;
									})}
								</tr>;
							})
						}
					</tbody>
				</table>;
			}
			return undefined;
		})}
	</>;

}

function FormTemplateGenericDisplayerPerFields(props: { fieldValues: InstanceField[], editMode: boolean, setRefresh: (refresh: boolean) => void }) {
	const { fieldValues } = props;
	const { template } = React.useContext(FormInstanceContext);
	const fields = useRecoilValue(AFormFields);
	const [fieldMap, setFieldMap] = React.useState<{ [key: string]: Field }>({});
	const [tree, setTree] = React.useState<Tree>({});
	const [allFields, setAllFields] = React.useState<Field[]>([]);
	const [componentMap, setComponentMap] = React.useState<{ [name: string]: T }>({});
	const [screenFieldMap, setFieldScreenMap] = React.useState<{ [id: number]: string }>({});

	React.useEffect(() => {
		getFields().then(setAllFields);
	}, []);

	React.useEffect(() => {
		const hiddenFields = template?.screens.reduce<Set<number>>((acc, screen) => {
			screen.fields.forEach(f => {
				if (f.hidden) {
					if (!f.field_id) {
						const id = fields.find(field => field.slug === f.slug)?.id;
						if (id) acc.add(id);
					}
					else
						acc.add(f.field_id);
				}
			});
			return acc;
		}, new Set()) ?? new Set();
		const componentMap = Object.entries<ComponentType | undefined>(fieldValues.reduce((acc, fv) => {
			Object.keys(fv.metadata).forEach(key => {
				if (acc[key] !== undefined || key === 'key') return;
				acc[key] = findDispatcherTypeFromMetadataKey(template, key);
			});
			return acc;
		}, {})).map(([name, type]) => ({
			name,
			type,
			precedence: getComponentTypePrecedence(type)
		})).sort((a, b) => b.precedence - a.precedence).reduce((acc, omk) => {
			acc[omk.name] = omk;
			return acc;
		}, {});
		setComponentMap(componentMap);
		setTree(buildTree(componentMap, fieldValues, hiddenFields));
	}, [template, fieldValues]);

	React.useEffect(() => {
		setFieldMap(fields.reduce((acc, field) => {
			acc[field.id] = { ...field, screen: screenFieldMap[field.id] };
			return acc;
		}, {}));
	}, [screenFieldMap]);

	React.useEffect(() => {
		const screenFieldMap: { [id: number]: string } = {};
		const fieldScreensMap: { [id: number]: string[] } = {};
		template?.screens.forEach(screen => {
			screen.fields.forEach(f => {
				let fieldId: number | undefined;
				if (f.field_id) {
					fieldId = f.field_id;
				} else if (f.slug) {
					const field = fields.find(field => field.slug === f.slug);
					if (field) fieldId = field.id;
				}
				if (fieldId !== undefined) {
					fieldScreensMap[fieldId] ??= [];
					fieldScreensMap[fieldId].push(screen.name ?? '');
				}
			});
		});
		Object.entries(fieldScreensMap).forEach(([id, screenNames]) => {
			screenFieldMap[parseInt(id)] = screenNames.length > 1 ? 'no-screen' : (screenNames[0] ?? '');
		});
		setFieldScreenMap(screenFieldMap);
	}, [template, fields]);

	return <TreeDisplayer tree={tree} fieldMap={fieldMap} componentMap={componentMap} editMode={props.editMode} allFieldsNoForm={allFields} setRefresh={props.setRefresh} />;
}
